// GeographicFocus — the consolidated location + level + borders block (US-878 UX).
//
// Replaces the three scattered controls (Selection Level, the duplicate
// Boundaries toggles, and the header breadcrumb working in isolation) with ONE
// coherent block: a compact "Viewing" path co-located with the level segmented,
// and a single rule — admin borders follow the active level (no separate
// Boundaries checkboxes). The header breadcrumb stays as global wayfinding; this
// is where the analyst sets focus. Reads everything from `useSelection`.
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import useSelection from "../../hooks/useSelection";
import SelectionLevel from "./SelectionLevel";

const GROUP_LABEL_SX = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "text.secondary",
};

// Compact focus path: country › province › district (the APAC root is dropped —
// the header breadcrumb carries it). Empty selection reads as the whole region.
const ViewingPath = ({ crumbs, hasSelection }) => {
  const parts = crumbs.slice(1).map((c) => c.label); // drop the "Asia-Pacific" root

  if (!hasSelection || parts.length === 0) {
    return (
      <Typography variant="caption" sx={{ color: "text.disabled" }}>
        All of Asia-Pacific
      </Typography>
    );
  }

  return (
    <Box sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {parts.map((label, index) => {
        const isLeaf = index === parts.length - 1;
        return (
          <Typography
            key={`${label}-${index}`}
            component="span"
            variant="caption"
            sx={{
              color: isLeaf ? "text.primary" : "text.secondary",
              fontWeight: isLeaf ? 600 : 400,
            }}
          >
            {index > 0 && (
              <Box component="span" aria-hidden sx={{ color: "text.disabled", mx: 0.5 }}>
                ›
              </Box>
            )}
            {label}
          </Typography>
        );
      })}
    </Box>
  );
};

const GeographicFocus = () => {
  const { crumbs, selection, clearSelection } = useSelection();
  const hasSelection = !!selection;

  return (
    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
      <Typography component="h3" sx={{ ...GROUP_LABEL_SX, mb: 1 }}>
        Geographic Focus
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mb: 1.25,
          minWidth: 0,
        }}
      >
        <PlaceOutlinedIcon
          sx={{
            fontSize: 16,
            flexShrink: 0,
            color: hasSelection ? "primary.main" : "text.disabled",
          }}
        />
        <ViewingPath crumbs={crumbs} hasSelection={hasSelection} />

        {/* Reset focus to the whole region — mirrors the header breadcrumb's
            Clear so the analyst can drop a selection without leaving the panel. */}
        {hasSelection && (
          <Button
            size="small"
            onClick={clearSelection}
            startIcon={<CloseRoundedIcon sx={{ fontSize: 15 }} />}
            aria-label="Clear selection and return to all of Asia-Pacific"
            sx={{
              ml: "auto",
              flexShrink: 0,
              fontSize: 12,
              fontWeight: 600,
              minWidth: 0,
              px: 0.75,
              py: 0.25,
              color: "primary.main",
              "& .MuiButton-startIcon": { mr: 0.25 },
              "&:hover": { bgcolor: "primary.light", color: "primary.dark" },
            }}
          >
            Clear
          </Button>
        )}
      </Box>

      <SelectionLevel />

      <Typography
        variant="caption"
        sx={{ display: "block", color: "text.secondary", mt: 1, lineHeight: 1.4 }}
      >
        Borders follow the selected level. Click the map to drill in.
      </Typography>
    </Box>
  );
};

export default GeographicFocus;
